/**
 * Note AI Edit Streaming API
 *
 * RTK Query API with fakeBaseQuery for SSE streaming using @microsoft/fetch-event-source
 */

import { getSSEPerformanceThreshold, matchNamespace } from '@features/ai-chat-core';
import { notesApi } from '@features/notes';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import SentryService from '@services/SentryService';
import { getAuth0AccessTokenSilently, isAuth0Initialized } from '@store/auth0BaseQuery';
import { Mutex } from 'async-mutex';

import type {
  ConfirmationType,
  EditPreview,
  MultiStepPayload,
  NoteChatRequestPayload,
  NoteChatSSEEvent,
  NoteChatStreamData,
} from '../types';

import {
  appendSectionContent,
  appendStreamingContent,
  clearAllActivities,
  clearAllSectionUpdates,
  clearInterrupt,
  completeWorkflow,
  finalizeMessage,
  finalizeSectionStreaming,
  markSectionAffected,
  type NoteAiEditState,
  saveSectionSnapshot,
  setActivitySnapshot,
  setAssistantStatus,
  setCurrentStep,
  setError,
  setSSEPerformanceMetrics,
  setThreadId,
  showInterrupt,
  startSectionStreaming,
  startStreamingMessage,
} from './chatSlice';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Idle watchdog timeout (5 minutes) — mirrors the note-generation stream path
// (`useNoteGenerationStreaming.ts`). Measures silence since the last inbound SSE
// event (re-armed on connection open and on every event), NOT total stream
// duration, so a long but actively-streaming run is never cut off. A chat stream
// can open but never emit a terminal RUN_STARTED/RUN_FINISHED (and `onerror`
// never fires), leaving the assistant "thinking" indefinitely with no telemetry.
// This bounds that silence and resolves it to a logged error instead.
const STREAM_TIMEOUT_MS = 5 * 60 * 1000;

// ============================================================================
// Auth Token Management (uses shared token function from auth0BaseQuery)
// ============================================================================

// Create a mutex to prevent multiple token refreshes
const mutex = new Mutex();

// ============================================================================
// Streaming API
// ============================================================================

export const noteChatStreamingApi = createApi({
  reducerPath: 'noteAiEdit/streaming',
  baseQuery: fakeBaseQuery(),
  endpoints: builder => ({
    /**
     * Stream note chat messages with interrupt workflow (AG-UI Protocol)
     *
     * Supports two AG-UI request types:
     * 1. Message Request - Initial user message or continuation
     * 2. Resume Request - Response to interrupts (section selection or approval)
     *
     * The stream stays open during interrupts, resuming when user responds.
     */
    streamNoteChat: builder.query<NoteChatStreamData, NoteChatRequestPayload>({
      // Initial cache state
      queryFn: () => ({
        data: {
          isActive: false,
          isDone: false,
        },
      }),

      async onCacheEntryAdded(
        payload,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { updateCachedData, cacheEntryRemoved, dispatch, getState },
      ) {
        const controller = new AbortController();

        // Extract noteId from payload
        const noteId = payload.noteId;
        if (!noteId) {
          dispatch(setError('No note ID provided'));
          return;
        }

        // Performance tracking: Capture request start time
        const requestStartTime = Date.now();
        const runId = payload.runId;

        // Performance tracking: Initialize timing state
        let connectionOpenTime: number | null = null;
        let firstEventTime: number | null = null;
        let firstTokenTime: number | null = null;
        let streamEndTime: number | null = null;
        let eventCount = 0;
        let outcome: 'success' | 'interrupt' | 'error' = 'success';
        let metricsDispatched = false; // Guard against duplicate dispatches
        
        // Configurable threshold via environment variable (default: 10 seconds)
        const thresholdMs = getSSEPerformanceThreshold();

        // Helper function to calculate and dispatch performance metrics
        const dispatchPerformanceMetrics = () => {
          // Guard against duplicate dispatches from multiple code paths
          if (metricsDispatched) {
            return;
          }
          
          if (!connectionOpenTime || !firstEventTime || !streamEndTime) {
            // Not enough data collected yet
            return;
          }
          
          metricsDispatched = true;

          const metrics = {
            time_to_connection_ms: connectionOpenTime - requestStartTime,
            time_to_first_event_ms: firstEventTime - requestStartTime,
            time_to_first_token_ms: firstTokenTime
              ? firstTokenTime - requestStartTime
              : firstEventTime - requestStartTime, // Fallback to first event if no token received
            total_duration_ms: streamEndTime - requestStartTime,
            run_id: runId,
            thread_id: payload.threadId,
            outcome,
            event_count: eventCount,
            exceeded_threshold: streamEndTime - requestStartTime > thresholdMs,
            threshold_ms: thresholdMs,
          };

          dispatch(setSSEPerformanceMetrics(metrics));
        };

        // Idle watchdog: guards against a stream that goes silent without a terminal
        // RUN_STARTED/RUN_FINISHED (and without firing `onerror`), which otherwise
        // leaves the assistant stuck "thinking" forever with no telemetry. Re-armed
        // on connection open and on every inbound event, so it measures idle time —
        // a legitimately long, still-streaming run is never aborted. Mirrors the
        // note-generation path's 5-minute timeout.
        let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
        let streamResolved = false; // Set once a terminal state (success/interrupt/error) is reached

        const clearWatchdog = () => {
          if (watchdogTimer) {
            clearTimeout(watchdogTimer);
            watchdogTimer = null;
          }
        };

        const armWatchdog = () => {
          // A resolved stream needs no watchdog — don't resurrect a cleared timer.
          if (streamResolved) {
            return;
          }
          clearWatchdog();
          watchdogTimer = setTimeout(() => {
            watchdogTimer = null;
            // Already finished normally — nothing to resolve.
            if (streamResolved) {
              return;
            }
            streamResolved = true;

            console.error('📡 [SSE] Note chat stream watchdog timeout', {
              noteId,
              threadId: payload.threadId,
              timeoutMs: STREAM_TIMEOUT_MS,
            });
            SentryService.logEvent('Note chat stream timed out', {
              level: 'error',
              tags: { streamPhase: 'note-chat', errorType: 'stream_timeout' },
              extra: {
                noteId,
                threadId: payload.threadId,
                runId,
                eventCount,
                timeoutMs: STREAM_TIMEOUT_MS,
              },
            });

            outcome = 'error';
            if (!streamEndTime) {
              streamEndTime = Date.now();
            }
            // setError resets assistant.status to 'idle', clearing the stuck spinner.
            dispatch(setError('The assistant timed out. Please try again.'));
            updateCachedData(draft => {
              draft.isActive = false;
            });
            dispatchPerformanceMetrics();
            // Abort the hung connection so fetchEventSource unwinds.
            controller.abort();
          }, STREAM_TIMEOUT_MS);
        };

        // Wait if there's another request currently refreshing the token
        await mutex.waitForUnlock();

        let token: string;
        try {
          // Use shared token function from auth0BaseQuery
          token = await getAuth0AccessTokenSilently();
        } catch (error) {
          console.error('Failed to get access token for streaming:', error);
          dispatch(setError('Authentication failed. Please log in again.'));
          outcome = 'error';
          streamEndTime = Date.now();
          dispatchPerformanceMetrics();
          return;
        }

        const streamUrl = `${API_BASE_URL}/v2/provider/notes/${noteId}/messages`;

        // Construct AG-UI request body (exclude noteId, it's only for URL)
        const { noteId: _, ...aguiPayload } = payload;

        // Track tool calls for logging
        const toolCallMap = new Map<
          string,
          {
            toolCallName: string;
            args: string;
          }
        >();

        // Start streaming
        (async () => {
          try {
            // Arm the idle watchdog before opening the connection; it is re-armed
            // on open and on every inbound event (idle timer, not a stream lifetime).
            armWatchdog();

            await fetchEventSource(streamUrl, {
              method: 'POST',
              signal: controller.signal,
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(aguiPayload),

              onopen: async res => {
                if (!res.ok) {
                  const errorText = await res.text();

                  // Handle authentication errors with retry logic (similar to auth0BaseQuery)
                  if (res.status === 401 || res.status === 403) {
                    // Check if mutex is locked to prevent multiple refreshes
                    if (!mutex.isLocked() && isAuth0Initialized()) {
                      const release = await mutex.acquire();

                      try {
                        // Try to get a fresh token, bypassing cache
                        // This refreshes the token for future connection attempts
                        // Note: fetchEventSource doesn't support retry in onopen, so we can't
                        // retry the current connection. The refreshed token will be used on the
                        // next user-initiated connection attempt.
                        await getAuth0AccessTokenSilently({ cacheMode: 'off' });

                        console.debug(
                          '📡 [SSE] Token refreshed, but connection already failed. User may retry.',
                        );
                        dispatch(
                          setError('Session expired. Please try again or refresh the page.'),
                        );
                      } catch (refreshError) {
                        // Handle token refresh failure
                        console.error('Failed to refresh authentication token:', refreshError);
                        dispatch(
                          setError('Session expired. Please refresh the page and log in again.'),
                        );
                      } finally {
                        // Release the mutex
                        release();
                      }
                    } else {
                      // Another request is refreshing, wait for it and show error
                      await mutex.waitForUnlock();
                      dispatch(setError('Session expired. Please try again or refresh the page.'));
                    }
                  } else {
                    dispatch(setError(`Failed to connect: ${res.status} ${res.statusText}`));
                  }

                  updateCachedData(draft => {
                    draft.isActive = false;
                  });

                  throw new Error(`Stream failed with status ${res.status}: ${errorText}`);
                }

                // Mark stream as active
                console.debug('📡 [SSE] Connection opened', { noteId, threadId: payload.threadId });

                // Connection established — restart the idle window.
                armWatchdog();

                // Performance tracking: Capture connection open time
                if (!connectionOpenTime) {
                  connectionOpenTime = Date.now();
                }

                updateCachedData(draft => {
                  draft.isActive = true;
                  draft.isDone = false;
                });
              },

              onmessage: event => {
                // Any inbound event counts as activity — reset the idle watchdog so
                // an actively-streaming run is never timed out mid-flight.
                armWatchdog();

                if (!event.data) return;

                // Performance tracking: Capture first event time
                if (!firstEventTime) {
                  firstEventTime = Date.now();
                }
                eventCount++;

                let data: NoteChatSSEEvent;
                try {
                  data = JSON.parse(event.data) as NoteChatSSEEvent;
                } catch {
                  console.warn('Failed to parse SSE event:', event.data);
                  return;
                }

                // Debug: Log every SSE event type received
                console.debug('📡 [SSE] Event received:', {
                  type: data.type,
                  keys: Object.keys(data),
                  data: data,
                });

                // Handle different event types
                switch (data.type) {
                  case 'RUN_STARTED': {
                    // AG-UI protocol: RUN_STARTED event with threadId (camelCase)
                    const state = getState() as Record<string, unknown>;
                    const noteAiEditState = state['noteAiEdit/ui'] as NoteAiEditState | undefined;
                    const currentThreadId = noteAiEditState?.threadId;
                    const newThreadId = 'threadId' in data ? data.threadId : null;
                    const isNewThread = newThreadId && newThreadId !== currentThreadId;

                    if (newThreadId) {
                      dispatch(setThreadId(newThreadId));
                    }
                    dispatch(setAssistantStatus('thinking'));

                    // Only clear section updates when starting a FRESH workflow (new thread)
                    // Don't clear when continuing existing workflow (approval/section responses)
                    // This prevents content from reverting before note data refreshes
                    if (isNewThread) {
                      console.debug('📡 [AG-UI] New thread started, clearing section updates');
                      dispatch(clearAllSectionUpdates());
                    }

                    // Clear CONFIRMATION interrupts immediately when backend acknowledges
                    // User has already made their choice, modal has all data in props,
                    // and clearing interrupt triggers modal close via useEffect in NoteChatView
                    const currentInterrupt = noteAiEditState?.interrupt;
                    if (currentInterrupt?.eventType === 'CONFIRMATION') {
                      console.debug('📡 [AG-UI] Clearing CONFIRMATION interrupt on RUN_STARTED');
                      dispatch(clearInterrupt());
                    }
                    break;
                  }

                  case 'STEP_STARTED':
                    if ('step_name' in data && data.step_name) {
                      dispatch(setCurrentStep(data.step_name));
                    }
                    break;

                  case 'ACTIVITY_SNAPSHOT': {
                    // Activity status update - display ephemeral activity bubble
                    if (data.type === 'ACTIVITY_SNAPSHOT') {
                      console.debug('📡 [AG-UI] Activity snapshot:', {
                        messageId: data.messageId,
                        activityType: data.activityType,
                        message: data.content.message,
                      });
                      dispatch(
                        setActivitySnapshot({
                          messageId: data.messageId,
                          activityType: data.activityType,
                          message: data.content.message,
                          timestamp: data.timestamp,
                          replace: data.replace,
                        }),
                      );
                    }
                    break;
                  }

                  case 'TEXT_MESSAGE_START': {
                    // Clear activities when actual message starts (activity → message transition)
                    dispatch(clearAllActivities());

                    // Check if this is a section update (namespace starts with 'section:')
                    const namespace = 'namespace' in data ? data.namespace : undefined;
                    const sectionIdFromNamespace = matchNamespace(namespace, 'section');
                    if (sectionIdFromNamespace) {
                      const sectionId = sectionIdFromNamespace;
                      console.debug('📡 [AG-UI] Section streaming START for:', sectionId);

                      // Capture current section content from note cache before AI edit
                      // Access RTK Query cache to get current section content before AI edit
                      const state = getState() as Record<string, unknown>;
                      const selector = notesApi.endpoints.getV2ProviderNotesByNoteId.select({
                        noteId,
                      });
                      // Type assertion needed: getState() returns generic RootState, but selector expects specific type
                      // The state structure is compatible at runtime, this is safe
                      // @ts-expect-error - RTK Query selector type mismatch with generic getState() return type
                      const noteData = selector(state)?.data;

                      if (noteData?.content?.sections) {
                        const foundSection = (
                          noteData.content.sections as Array<{
                            section_id: string;
                            edited_text?: string;
                            text?: string;
                          }>
                        ).find(s => s.section_id === sectionId);

                        if (foundSection) {
                          const currentContent =
                            foundSection.edited_text || foundSection.text || '';
                          dispatch(saveSectionSnapshot({ sectionId, content: currentContent }));
                          console.debug('📡 [AG-UI] Saved section snapshot:', {
                            sectionId,
                            contentLength: currentContent.length,
                          });
                        }
                      }

                      dispatch(startSectionStreaming(sectionId));
                    } else {
                      // Regular assistant message
                      dispatch(
                        startStreamingMessage(
                          'messageId' in data && data.messageId ? data.messageId : '',
                        ),
                      );
                      dispatch(setAssistantStatus('streaming'));
                    }
                    break;
                  }

                  case 'TEXT_MESSAGE_CONTENT': {
                    if (!('delta' in data) || !data.delta) break;

                    // Performance tracking: Capture first token time
                    if (!firstTokenTime) {
                      firstTokenTime = Date.now();
                    }

                    // Check if this is a section update using shared utility
                    const contentNamespace = 'namespace' in data ? data.namespace : undefined;
                    const contentSectionId = matchNamespace(contentNamespace, 'section');
                    if (contentSectionId) {
                      // Section update content
                      console.debug(
                        '📡 [AG-UI] Section streaming CONTENT for:',
                        contentSectionId,
                        'delta length:',
                        data.delta.length,
                      );
                      dispatch(
                        appendSectionContent({
                          sectionId: contentSectionId,
                          content: data.delta,
                        }),
                      );
                    } else {
                      // Regular assistant message content
                      dispatch(appendStreamingContent(data.delta));
                    }
                    break;
                  }

                  case 'TEXT_MESSAGE_END': {
                    // Check if this is a section update using shared utility
                    const endNamespace = 'namespace' in data ? data.namespace : undefined;
                    const endSectionId = matchNamespace(endNamespace, 'section');
                    if (endSectionId) {
                      // Section streaming complete
                      console.debug('📡 [AG-UI] Section streaming END for:', endSectionId);
                      // Mark section as affected (has pending AI edits)
                      dispatch(markSectionAffected(endSectionId));
                      // Mark section as no longer actively streaming (removes from streamingSections)
                      // Keep content in sectionUpdates for display until user approves/reverts
                      dispatch(finalizeSectionStreaming(endSectionId));
                    } else {
                      // Regular assistant message complete
                      dispatch(finalizeMessage());
                      dispatch(setAssistantStatus('thinking'));
                    }
                    break;
                  }

                  case 'RUN_FINISHED': {
                    // Performance tracking: Capture stream end time
                    if (!streamEndTime) {
                      streamEndTime = Date.now();
                    }

                    // AG-UI protocol: Check for interrupt
                    // Backend may send reason="multi_step" for MULTI_STEP interrupts
                    const isInterrupt =
                      data.outcome === 'interrupt' || (data as any).reason === 'multi_step';
                    if (isInterrupt && data.interrupt) {
                      outcome = 'interrupt';
                      const { id, payload } = data.interrupt;
                      const payloadWithFallback = payload as any;

                      console.debug('📡 [AG-UI] Interrupt received:', {
                        id,
                        eventType: payload.eventType || payloadWithFallback.event_type,
                        reason: (data as any).reason,
                        outcome: data.outcome,
                        payload,
                      });

                      // Dispatch unified interrupt based on eventType discriminator
                      if (payload.eventType === 'CONFIRMATION') {
                        // CONFIRMATION interrupt (e.g., approval workflow)
                        // Cast to note-specific types since backend sends section_id, not entity_id
                        const edits = (payload.metadata?.edits || []) as unknown as EditPreview[];
                        const confirmationType = payload.confirmationType as ConfirmationType | undefined;
                        dispatch(
                          showInterrupt({
                            interruptId: id,
                            eventType: 'CONFIRMATION',
                            confirmLabel: payload.confirmLabel,
                            cancelLabel: payload.cancelLabel,
                            message: payload.message,
                            edits,
                            namespace: payload.namespace,
                            confirmationType,
                          }),
                        );
                      } else if (payload.eventType === 'MULTIPLE_CHOICE') {
                        // MULTIPLE_CHOICE interrupt (e.g., section selection)
                        dispatch(
                          showInterrupt({
                            interruptId: id,
                            eventType: 'MULTIPLE_CHOICE',
                            question: payload.question,
                            options: payload.options,
                            allowMultiple: payload.allowMultiple,
                            allowSelectAll: payload.allowSelectAll,
                            minSelections: payload.minSelections,
                            maxSelections: payload.maxSelections,
                            allowTextInput: payload.allowTextInput,
                            textInputPlaceholder: payload.textInputPlaceholder,
                          }),
                        );
                      } else if (
                        payload.eventType === 'MULTI_STEP' ||
                        payloadWithFallback.event_type === 'MULTI_STEP'
                      ) {
                        // MULTI_STEP interrupt (wizard with multiple steps)
                        const multiStepPayload = payload as MultiStepPayload;
                        dispatch(
                          showInterrupt({
                            interruptId: id,
                            eventType: 'MULTI_STEP',
                            steps: multiStepPayload.steps,
                            title: multiStepPayload.title,
                          }),
                        );
                      } else if (payload.eventType === 'EXPRESS_INTEREST') {
                        // EXPRESS_INTEREST interrupt (shows "I'm Interested" button)
                        dispatch(
                          showInterrupt({
                            interruptId: id,
                            eventType: 'EXPRESS_INTEREST',
                            detectedIntent: payload.detectedIntent,
                            originalMessage: payload.originalMessage,
                            metadata: payload.metadata,
                          }),
                        );
                      }

                      // Terminal for this connection — stop the watchdog.
                      streamResolved = true;
                      clearWatchdog();

                      // Dispatch performance metrics before closing
                      dispatchPerformanceMetrics();

                      // Close connection - no more events coming, user will send new request
                      console.debug('📡 [AG-UI] Interrupt handled, closing connection');
                      controller.abort();
                      break;
                    }

                    // Normal completion (no interrupt) — stop the watchdog.
                    streamResolved = true;
                    clearWatchdog();
                    outcome = 'success';
                    console.debug('📡 [AG-UI] Run finished successfully, closing connection');
                    dispatch(clearAllActivities()); // Clear any remaining activities
                    dispatch(completeWorkflow());

                    // Force immediate note refetch to sync section content with backend
                    // This ensures rejected changes revert and approved changes persist
                    dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));

                    updateCachedData(draft => {
                      draft.isDone = true;
                      draft.isActive = false;
                    });

                    // Dispatch performance metrics before closing
                    dispatchPerformanceMetrics();

                    // Close connection immediately - workflow is complete
                    controller.abort();
                    break;
                  }

                  case 'TOOL_CALL_START': {
                    // Tool call started - track it
                    if (data.type === 'TOOL_CALL_START') {
                      toolCallMap.set(data.toolCallId, {
                        toolCallName: data.toolCallName,
                        args: '',
                      });
                      console.debug('📡 [AG-UI] Tool call started:', {
                        toolCallId: data.toolCallId,
                        toolCallName: data.toolCallName,
                      });
                    }
                    break;
                  }

                  case 'TOOL_CALL_ARGS': {
                    // Tool call arguments (streaming delta) - accumulate args
                    if (data.type === 'TOOL_CALL_ARGS') {
                      const toolCall = toolCallMap.get(data.toolCallId);
                      if (toolCall) {
                        toolCall.args += data.delta;
                      } else {
                        // Tool call not tracked, create entry
                        toolCallMap.set(data.toolCallId, {
                          toolCallName: 'unknown',
                          args: data.delta,
                        });
                      }
                    }
                    break;
                  }

                  case 'TOOL_CALL_END': {
                    // Tool call completed - extract and log important info
                    if (data.type === 'TOOL_CALL_END') {
                      const toolCall = toolCallMap.get(data.toolCallId);
                      if (toolCall) {
                        try {
                          const payload = JSON.parse(toolCall.args);

                          // Generic tool call logging
                          console.log('🔧 [Tool Call]', {
                            toolCallName: toolCall.toolCallName,
                            toolCallId: data.toolCallId,
                            payload,
                          });
                        } catch (error) {
                          console.warn('📡 [AG-UI] Failed to parse tool call args:', toolCall.args, error);
                        }
                        // Clean up
                        toolCallMap.delete(data.toolCallId);
                      } else {
                        console.debug('📡 [AG-UI] Tool call ended (no tracked data):', {
                          toolCallId: data.toolCallId,
                        });
                      }
                    }
                    break;
                  }

                  case 'error': {
                    streamResolved = true;
                    clearWatchdog();
                    outcome = 'error';
                    if (!streamEndTime) {
                      streamEndTime = Date.now();
                    }
                    const errorMessage =
                      'message' in data && data.message ? data.message : 'Unknown error';
                    SentryService.logEvent('Note chat stream error event', {
                      level: 'error',
                      tags: { streamPhase: 'note-chat', errorType: 'stream_error_event' },
                      extra: { noteId, threadId: payload.threadId, runId, message: errorMessage },
                    });
                    dispatch(setError(errorMessage));
                    updateCachedData(draft => {
                      draft.isActive = false;
                    });
                    dispatchPerformanceMetrics();
                    break;
                  }

                  default:
                    // Unknown event type - ignore
                    break;
                }
              },

              onerror: err => {
                streamResolved = true;
                clearWatchdog();
                outcome = 'error';
                if (!streamEndTime) {
                  streamEndTime = Date.now();
                }
                console.error('Note chat stream error:', err);
                SentryService.captureException(err, {
                  level: 'error',
                  tags: { streamPhase: 'note-chat', errorType: 'sse_disconnect' },
                  extra: { noteId, threadId: payload.threadId, runId, eventCount },
                });
                dispatch(setError('Stream connection failed'));
                dispatchPerformanceMetrics();
                controller.abort();
              },
            });
          } catch (error) {
            // Distinguish between intentional abort (cleanup) and actual errors
            if (error instanceof Error && error.name === 'AbortError') {
              console.debug('📡 [SSE] Stream connection closed (aborted)');
              // Don't dispatch metrics for intentional aborts (they're already dispatched)
            } else {
              outcome = 'error';
              if (!streamEndTime) {
                streamEndTime = Date.now();
              }
              console.error('Note chat stream failed:', error);
              SentryService.captureException(error, {
                level: 'error',
                tags: { streamPhase: 'note-chat', errorType: 'connection_failure' },
                extra: { noteId, threadId: payload.threadId, runId, eventCount },
              });
              dispatch(setError(error instanceof Error ? error.message : 'Stream failed'));
              dispatchPerformanceMetrics();
            }

            updateCachedData(draft => {
              draft.isActive = false;
            });
          } finally {
            // Whatever path unwound the stream, the watchdog is no longer needed.
            streamResolved = true;
            clearWatchdog();
          }
        })();

        // Wait for cache entry removal
        await cacheEntryRemoved;

        // Cleanup
        streamResolved = true;
        clearWatchdog();
        controller.abort();
        dispatch(setAssistantStatus('idle'));
      },
    }),
  }),
});

// ============================================================================
// Exports
// ============================================================================

export const { useStreamNoteChatQuery } = noteChatStreamingApi;
